// D98.1 / D98.4: the Lines grid that replaces the old 32-column table in ClientInvoicePage.
// Presentational only: no fetches and no money maths of its own. Every figure comes in through props
// (the page's `getRowPricing` for cost / price / mark-up / line total, `billedQuantity` for Billed).
//
// Prop contract (CIP passes its EXISTING handlers straight in):
//   items                 invoice.items
//   currency              invoice currency (formatCurrency + the cost and selling-price headers; '' = a bare number, D96.14)
//   columnWidths          the page's useColumnWidths('client-invoice-lines'): this person's widths and the drag's save (D178)
//   pricingFor(item)      = getRowPricing -> { costPrice, sellingPrice, markupPercent, makesMarkupPercent, lineTotal } (null = missing)
//   severities[index]     'block' | 'warn' | 'done' | 'none' - computed elsewhere, only drawn here: a small diamond
//                         (red blocks sending, amber needs a look). The round dot is the line's itemStatus colour (D98.4).
//   unpickedLines         line numbers lineIssues.unpickedSupplierLines reports (a name with no vendor id) - the ONLY red supplier box
//   updateItem(i, patch)  Done tick (completedTick) and supplier ({supplierName, supplierVendorId})
//   setDone(indexes, done) the page's ONE batched edit (one undo step) of completedTick: the Status header's tick marks
//                         every shown line Done, or clears them all when all are (owner 2026-11-02). Omitted = no header tick
//   onCommitCost(i, text) = updateCostPrice      costEntryOf(item, i) 'ttd' = the cost cell is read-only (D152.2)
//   onCommitPrice(i, text) = updateSellingPrice
//   onCommitMarkup(i, text)  the page sets markupOverridden (no confirm, D98.11) and stores the typed rate (D119.15)
//   removalReason(item, i) = lineRemovalLock -> reason string or null     onRemove(i): the page keeps its confirm
//   onOpenLine(i)         row click / Enter / menu "Open"                 selectedIndex: highlighted row
//   linesLocked           sent invoice: price, mark-up, remove locked; cost, supplier, Done stay editable (D96.2)
//   readOnly              cancelled invoice (D96.18): every control disabled
//   visibleIndexes        optional filter ("Show only these")
//   creditedShortByLine   shortDeliveryCreditedQtyByLine(creditNotes), so Pending nets a short-delivery credit as the Delivery
//                         tab does; omitted = no credits
//   hidesSupplierBilled   the page's rule for when the supplier's billed figure is not shown (a credited line, a moved
//                         unit), the same the drawer's "Bill X" reads; Short by is blank there. Omitted = always shown
//   headerTop             px the column header sticks at in the page pane, right under the Lines tab's sticky tools (D185.6).
//                         Omitted = flush with the pane's top edge (-top-4)
import React from 'react';
import { createPortal } from 'react-dom';
import type { ClientInvoiceItemDto } from '../../lib/clientInvoiceApi';
import { currencyPrefix, formatCurrency } from '../../utils/priceUtils';
import { billedQuantity } from '../../utils/clientInvoiceLine';
import VendorSelect from '../../components/VendorSelect';
import { DraftInput } from './DraftInput';
import { STATUS_OPTIONS } from './LineDrawer';
import { costInputText, type CostEntry } from './lineCostCurrency';
import { descriptionDiffersFromName } from './lineText';
import { costColumnLabel, sellingPriceLabel } from './linePriceLabel';
import { CostSourceTag } from './CostSourceTag';
import { remainingDispatchQty } from '../../utils/clientInvoiceDispatch';
import { supplierShortBy } from './supplierShort';
import { readsAsInventory, INVENTORY_SUPPLIER } from './inventorySupplier';
import { ColumnResizeGrip } from '../../components/ColumnResizeGrip';
import type { ColumnWidthsState } from '../../hooks/useColumnWidths';
import { resolveTableLayout } from '../../utils/tableColumns';

export type LineSeverity = 'block' | 'warn' | 'done' | 'none';

export interface LinePricing {
  costPrice: number | null;
  sellingPrice: number | null;
  markupPercent: number | null;
  /** D119.15: set only while `markupPercent` is a typed rate that the sale realises differently: the actual %. */
  makesMarkupPercent?: number | null;
  lineTotal: number | null;
}

export interface LinesGridProps {
  items: ClientInvoiceItemDto[];
  currency: string;
  columnWidths: ColumnWidthsState;
  pricingFor: (item: ClientInvoiceItemDto) => LinePricing;
  severities?: LineSeverity[];
  unpickedLines?: number[];
  selectedIndex?: number | null;
  visibleIndexes?: number[] | null;
  creditedShortByLine?: Map<string, number> | null;
  hidesSupplierBilled?: (item: ClientInvoiceItemDto, index: number) => boolean;
  linesLocked?: boolean;
  readOnly?: boolean;
  updateItem: (index: number, patch: Partial<ClientInvoiceItemDto>) => void;
  setDone?: (indexes: number[], done: boolean) => void;
  onCommitCost: (index: number, text: string) => void;
  /**
   * The line's cost in TT$, for the TT$ cost column beside Cost (the page's one reader of it, shared with the line
   * drawer). Omitted = no such column (a TT$ invoice's Cost is already TT$). `text` is "" when it cannot be stated, with the
   * reason, so a blank is never read as zero.
   */
  ttdCostOf?: (item: ClientInvoiceItemDto) => { text: string; reason: string | null };
  /** D152.2: where each line's cost is typed (the page's `costEntryFor`); 'ttd' = the cost cell is read-only text. Omitted = 'single'. */
  costEntryOf?: (item: ClientInvoiceItemDto, index: number) => CostEntry;
  onCommitPrice: (index: number, text: string) => void;
  onCommitMarkup: (index: number, text: string) => void;
  removalReason: (item: ClientInvoiceItemDto, index: number) => string | null;
  onRemove: (index: number) => void;
  onOpenLine: (index: number) => void;
  headerTop?: number;
}

// The columns drawn, in order: stable key (saved widths are by key, so they survive a currency change, D178), header,
// default width and alignment. width null = Item takes the rest (tableColumns). D152.1: the price header names its currency,
// so the list is built per render. Description and Packing details sit right after Item and print what the invoice prints
// for the line, so ~200 lines can be checked without opening each one; each is drawn only when some line has it (D178).
// The cost header names its currency too (D171.8); with the TT$ cost column (`ttdCostOf`) "Cost (TT$)" sits straight
// before it. The trailing actions column has no header text and no grip.
interface GridColumn { key: string; header: string; width: number | null; right?: boolean }
const columnsFor = (currency: string, withTtd: boolean, withDescription: boolean, withPacking: boolean): GridColumn[] => [
  { key: 'number', header: '#', width: 40 },
  { key: 'status', header: 'Status', width: 64 },
  { key: 'item', header: 'Item', width: null },
  ...(withDescription ? [{ key: 'description', header: 'Description', width: 140 }] : []),
  ...(withPacking ? [{ key: 'packing', header: 'Packing details', width: 130 }] : []),
  { key: 'supplier', header: 'Supplier', width: 180 },
  { key: 'ordered', header: 'Ordered', width: 84, right: true },
  { key: 'billed', header: 'Billed', width: 84 },
  // Monday demo (meeting 2026-10-31): still to send (remainingDispatchQty, the Delivery tab's Remaining) and the stored
  // supplier shortage (supplierShortBy). Shown only; nothing is accepted or refused from them.
  { key: 'pending', header: 'Pending', width: 84, right: true },
  { key: 'shortBy', header: 'Short by', width: 84, right: true },
  ...(withTtd ? [{ key: 'costTtd', header: 'Cost (TT$)', width: 96, right: true }] : []),
  { key: 'cost', header: costColumnLabel(currency), width: 104, right: true }, // with room for the bill tag (D171.8)
  { key: 'markup', header: 'Mark-up %', width: 96, right: true },
  { key: 'price', header: sellingPriceLabel(currency), width: 140, right: true },
  { key: 'lineTotal', header: 'Line total', width: 110, right: true },
];
const ACTIONS_COLUMN = { key: 'actions', width: 36 };

// What a line prints as its description: nothing when it only repeats the name (the panel's rule). Cell and column test read it.
const printedDescription = (item: ClientInvoiceItemDto) =>
  descriptionDiffersFromName(item.itemName, item.itemDescription) ? item.itemDescription : '';
const hasText = (text: string | null | undefined) => !!text?.trim();

// Issue severity marker; done / none draw nothing (the Done tick is its own control).
const MARKER: Partial<Record<LineSeverity, { cls: string; label: string }>> = {
  block: { cls: 'bg-rose-500', label: 'Blocks sending' },
  warn: { cls: 'bg-amber-400', label: 'Needs a look' },
};
// The itemStatus colour dot (D98.4: the line status colour shows as the row's dot).
const STATUS_DOT: Record<string, string> = {
  red: 'bg-red-600', purple: 'bg-purple-500', pink: 'bg-pink-400', orange: 'bg-orange-500', blue: 'bg-blue-500',
};

const editable = (n: number | null) => (n == null || !Number.isFinite(n) ? '' : n.toFixed(2));
const INTERACTIVE = 'input,button,select,textarea,a,[role="listbox"],[role="menu"]';

function RowMenu({ index, disabled, removeReason, removeDisabled, onOpen, onRemove }: {
  index: number;
  disabled: boolean;
  removeReason: string | null;
  removeDisabled: boolean;
  onOpen: () => void;
  onRemove: () => void;
}) {
  const [pos, setPos] = React.useState<{ top: number; right: number } | null>(null);
  const btnRef = React.useRef<HTMLButtonElement | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const close = () => setPos(null);
  React.useEffect(() => {
    if (!pos) return undefined;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!menuRef.current?.contains(t) && !btnRef.current?.contains(t)) setPos(null);
    };
    const onScroll = () => setPos(null);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [pos]);
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        aria-label={`Row actions, line ${index + 1}`}
        aria-haspopup="menu"
        aria-expanded={!!pos}
        onClick={() => {
          const r = btnRef.current?.getBoundingClientRect();
          setPos(pos || !r ? null : { top: r.bottom + 4, right: window.innerWidth - r.right });
        }}
        className="h-8 w-8 rounded text-slate-500 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
      >
        ⋯
      </button>
      {pos &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            className="fixed z-50 w-56 rounded-md border border-slate-200 bg-white py-1 text-xs shadow-lg"
            style={{ top: pos.top, right: pos.right }}
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                close();
                onOpen();
              }}
              className="block w-full px-3 py-2 text-left text-slate-700 hover:bg-slate-50"
            >
              Open
            </button>
            <button
              type="button"
              role="menuitem"
              disabled={removeDisabled}
              title={removeReason ?? undefined}
              onClick={() => {
                close();
                onRemove();
              }}
              className="block w-full px-3 py-2 text-left text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:text-slate-400 disabled:hover:bg-transparent"
            >
              Remove
            </button>
            {removeReason && <p className="px-3 pb-2 text-xs text-slate-500">{removeReason}</p>}
          </div>,
          document.body
        )}
    </>
  );
}

export function LinesGrid(props: LinesGridProps) {
  const {
    items, currency, columnWidths, pricingFor, severities = [], unpickedLines = [], selectedIndex = null, visibleIndexes = null,
    linesLocked = false, readOnly = false, updateItem, onCommitCost, onCommitPrice, onCommitMarkup,
    removalReason, onRemove, onOpenLine, costEntryOf, ttdCostOf, creditedShortByLine = null, hidesSupplierBilled, setDone, headerTop,
  } = props;
  const withTtd = !!ttdCostOf;
  // D178: over all `items`, never the "Show only these" filter, so filtering does not make columns jump.
  const withDescription = items.some((item) => hasText(printedDescription(item)));
  const withPacking = items.some((item) => hasText(item.itemPackingDetails));
  const columns = columnsFor(currency, withTtd, withDescription, withPacking);
  const layout = resolveTableLayout([...columns, ACTIONS_COLUMN], columnWidths.widths);
  const money = (v: number) => formatCurrency(v, currency || null, { ceil: false });
  const shown = items
    .map((item, index) => ({ item, index }))
    .filter(({ index }) => !visibleIndexes || visibleIndexes.includes(index));
  const priceLock = readOnly || linesLocked;
  // The Status header's tick (derived, stored nowhere): ticked when every shown line is Done, a dash when some are.
  const doneShown = shown.filter(({ item }) => !!item.completedTick).map(({ index }) => index);
  const allDone = shown.length > 0 && doneShown.length === shown.length;
  const toggleAllDone = () => {
    const indexes = allDone ? doneShown : shown.filter(({ item }) => !item.completedTick).map(({ index }) => index);
    if (setDone && indexes.length > 0) setDone(indexes, !allDone);
  };

  return (
    // No scroll box of its own: the page pane scrolls, and the sticky header sticks to it at headerTop (D185.6).
    <div className="rounded-md border border-slate-200">
      <table className="data-table-grid table-fixed text-xs [&_td]:overflow-hidden [&_td]:[overflow-wrap:anywhere]" style={layout.tableStyle}>
        <colgroup>
          {[...columns, ACTIONS_COLUMN].map(({ key }) => (
            <col key={key} style={layout.widths[key] ? { width: layout.widths[key] } : undefined} />
          ))}
        </colgroup>
        <thead
          className="sticky -top-4 z-10 bg-slate-50 text-left text-slate-600 shadow-[0_1px_0_0_#e2e8f0]"
          style={headerTop === undefined ? undefined : { top: headerTop }}
        >
          <tr>
            {columns.map((c) => (
              <th key={c.key} scope="col" className={`relative overflow-hidden whitespace-nowrap px-2 py-2 font-semibold ${c.right ? 'text-right' : ''}`}>
                {c.key === 'status' && setDone ? (
                  <label className="inline-flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      aria-label="Mark every shown line done"
                      title={allDone ? 'Clear Done on every shown line' : 'Mark every shown line done'}
                      checked={allDone}
                      ref={(el) => { if (el) el.indeterminate = !allDone && doneShown.length > 0; }}
                      disabled={readOnly || shown.length === 0}
                      onChange={toggleAllDone}
                    />
                    {c.header}
                  </label>
                ) : (
                  c.header
                )}
                <ColumnResizeGrip
                  onResize={(px) => columnWidths.resize(c.key, px)}
                  onReset={() => columnWidths.reset(c.key)}
                  onResizeEnd={columnWidths.commit}
                />
              </th>
            ))}
            <th scope="col" className="px-2 py-2">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {shown.length === 0 ? (
            <tr>
              <td colSpan={columns.length + 1} className="px-3 py-8 text-center text-slate-500">
                No lines to show.
              </td>
            </tr>
          ) : (
            shown.map(({ item, index }) => {
              const pricing = pricingFor(item);
              const sev = severities[index] || 'none';
              const reason = removalReason(item, index);
              const selected = selectedIndex === index;
              const billed = billedQuantity(item) ?? 0;
              const billedUnit = item.billUnit || item.packingUnit || item.unit || '';
              const pending = remainingDispatchQty(item, creditedShortByLine?.get(String(item.lineNumber ?? index + 1)) || 0);
              const shortBy = hidesSupplierBilled?.(item, index) ? null : supplierShortBy(item);
              // The unit the line drawer's "Bill X" and the server's alert name the supplier's figure in.
              const shortUnit = item.billUnit || item.unit || '';
              const done = !!item.completedTick;
              const unpicked = unpickedLines.includes(item.lineNumber ?? index + 1);
              // D161.7: the mark-up is yellow only while its box can be typed in; the Item cell's badge stays.
              const markupDisabled = priceLock || !((pricing.costPrice ?? 0) > 0);
              return (
                <tr
                  key={`${item.lineNumber ?? index}`}
                  tabIndex={0}
                  aria-selected={selected}
                  data-line-index={index}
                  onClick={(e) => {
                    if (!(e.target as HTMLElement).closest(INTERACTIVE)) onOpenLine(index);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.target === e.currentTarget) onOpenLine(index);
                  }}
                  className={`cursor-pointer border-b border-slate-100 align-middle outline-none focus:bg-slate-50 ${
                    selected ? 'bg-primary-50 ring-1 ring-inset ring-primary-300' : 'bg-white hover:bg-slate-50'
                  }`}
                >
                  <td className="px-2 py-2 text-slate-600">
                    {item.lineNumber ?? index + 1}
                    {(item as any).isManualAddition && (
                      <span
                        className="ml-1 font-bold text-fuchsia-700"
                        title="Added to invoice (not on the original confirmed order)"
                        aria-label="Added manually"
                      >
                        +
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-2">
                      {item.itemStatus && STATUS_DOT[item.itemStatus] && (
                        <span
                          role="img"
                          aria-label={`Status: ${STATUS_OPTIONS.find((o) => o.value === item.itemStatus)?.label ?? item.itemStatus}`}
                          title={STATUS_OPTIONS.find((o) => o.value === item.itemStatus)?.label ?? item.itemStatus}
                          data-status={item.itemStatus}
                          className={`inline-block h-2.5 w-2.5 rounded-full ${STATUS_DOT[item.itemStatus]}`}
                        />
                      )}
                      {MARKER[sev] && (
                        <span
                          role="img"
                          aria-label={MARKER[sev]!.label}
                          title={MARKER[sev]!.label}
                          data-severity={sev}
                          className={`inline-block h-2 w-2 rotate-45 ${MARKER[sev]!.cls}`}
                        />
                      )}
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={done}
                        aria-label={`Done, line ${index + 1}`}
                        title="Tick when this line is fully finalised"
                        disabled={readOnly}
                        onClick={() => updateItem(index, { completedTick: !done })}
                        className={`flex h-5 w-5 items-center justify-center rounded border text-xs font-bold disabled:cursor-not-allowed disabled:opacity-50 ${
                          done
                            ? 'border-primary-500 bg-primary-500 text-white'
                            : 'border-slate-300 bg-white text-transparent hover:border-slate-400'
                        }`}
                      >
                        ✓
                      </button>
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <div className="truncate font-medium text-slate-800" title={item.itemName || ''}>
                      {item.itemName || <span className="text-rose-600">No item name</span>}
                      {item.markupOverridden && (
                        <span
                          className="ml-2 rounded bg-yellow-100 px-1.5 py-0.5 text-xs font-semibold text-yellow-800"
                          title="Mark-Up % was manually overridden"
                        >
                          overridden
                        </span>
                      )}
                    </div>
                    {/* D152.4: the client remark prints under the item; description and packing have their own columns. */}
                    {item.clientRemarks?.trim() && (
                      <div className="truncate text-slate-500" title={item.clientRemarks}>
                        {item.clientRemarks}
                      </div>
                    )}
                  </td>
                  {[
                    ...(withDescription ? [{ label: 'Description', text: printedDescription(item) }] : []),
                    ...(withPacking ? [{ label: 'Packing details', text: item.itemPackingDetails }] : []),
                  ].map(({ label, text }) => (
                    <td key={label} className="px-2 py-2 align-middle text-slate-600">
                      {hasText(text) ? (
                        <div className="line-clamp-2 break-words" aria-label={`${label}, line ${index + 1}`} title={text}>
                          {text}
                        </div>
                      ) : (
                        <span className="text-slate-300" aria-label={`${label}, line ${index + 1}`}>—</span>
                      )}
                    </td>
                  ))}
                  <td className="px-2 py-2">
                    {/* D86: picked from the vendor list, never typed. Editable on a sent invoice (D96.2). */}
                    <VendorSelect
                      compact
                      value={{ vendorId: item.supplierVendorId ?? null, name: item.supplierName || '' }}
                      onChange={(v) => updateItem(index, { supplierName: v.name, supplierVendorId: v.vendorId })}
                      disabled={readOnly}
                      placeholder={readsAsInventory(item) ? INVENTORY_SUPPLIER : 'Pick supplier'}
                      className={unpicked && !readOnly ? '[&_input]:border-red-500 [&_input]:placeholder-red-500' : ''}
                    />
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-slate-700">
                    {item.confirmedQuantity ?? 0} <span className="text-slate-500">{item.unit || ''}</span>
                  </td>
                  <td className="px-2 py-2 tabular-nums text-slate-700">
                    {billed} <span className="text-slate-500">{billedUnit}</span>
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-slate-700" aria-label={`Pending, line ${index + 1}`}>
                    {pending} <span className="text-slate-500">{billedUnit}</span>
                  </td>
                  {shortBy === null ? (
                    <td className="px-2 py-2 text-right text-slate-300" aria-label={`Short by, line ${index + 1}`}>—</td>
                  ) : (
                    <td
                      className="px-2 py-2 text-right font-semibold tabular-nums text-amber-700"
                      aria-label={`Short by, line ${index + 1}`}
                      title={`The supplier billed ${item.supplierBilledQty} ${shortUnit} of ${item.confirmedQuantity} ${shortUnit} ordered. Nothing changes until someone bills it.`.replace(/ +/g, ' ')}
                    >
                      {shortBy} <span className="font-normal">{shortUnit}</span>
                    </td>
                  )}
                  {ttdCostOf && (
                    <td className="px-2 py-2 text-right tabular-nums text-slate-700">
                      {(() => {
                        const ttd = ttdCostOf(item);
                        return (
                          <span aria-label={`Cost (TT$), line ${index + 1}`} title={ttd.reason ?? undefined}>
                            {ttd.text ? `${currencyPrefix('TTD')}${ttd.text}` : '—'}
                          </span>
                        );
                      })()}
                    </td>
                  )}
                  <td className="px-2 py-2 text-right">
                    {/* D96.2: cost is USC's, not the client's money - it stays editable after sending.
                        D171.8/D172.1: a cost is tagged and coloured by its source beside the cost, never inside it (staff only). */}
                    <div className="flex items-center gap-1">
                      <CostSourceTag item={item} />
                      {costEntryOf?.(item, index) === 'ttd' ? (
                        // D152.2: the cost is typed in TT$ in the line drawer; this US$ figure is worked out from it.
                        <span
                          aria-label={`Cost, line ${index + 1}`}
                          title="Worked out from the TT$ cost - type the TT$ cost in the line drawer"
                          className="block min-w-0 flex-1 px-2 py-1.5 text-right tabular-nums text-slate-700"
                        >
                          {costInputText(pricing.costPrice)}
                        </span>
                      ) : (
                        <DraftInput
                          aria-label={`Cost, line ${index + 1}`}
                          inputMode="decimal"
                          disabled={readOnly}
                          value={costInputText(pricing.costPrice)}
                          onCommit={(t) => onCommitCost(index, t)}
                          className="h-8 w-full min-w-0 flex-1 rounded border border-slate-200 bg-white px-2 text-right text-xs disabled:bg-slate-50"
                        />
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-right">
                    <DraftInput
                      aria-label={`Mark-up %, line ${index + 1}`}
                      inputMode="decimal"
                      disabled={markupDisabled}
                      value={editable(pricing.markupPercent)}
                      onCommit={(t) => onCommitMarkup(index, t)}
                      className={`h-8 w-full rounded border px-2 text-right text-xs disabled:bg-slate-50 ${
                        item.markupOverridden && !markupDisabled ? 'border-yellow-400 bg-yellow-100 font-semibold' : 'border-slate-200 bg-white'
                      }`}
                    />
                    {pricing.makesMarkupPercent != null && (
                      <div className="mt-0.5 text-xs text-slate-500">makes {pricing.makesMarkupPercent.toFixed(2)}%</div>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right">
                    <DraftInput
                      aria-label={`${sellingPriceLabel(currency)}, line ${index + 1}`}
                      inputMode="decimal"
                      disabled={priceLock}
                      value={editable(pricing.sellingPrice)}
                      onCommit={(t) => onCommitPrice(index, t)}
                      className="h-8 w-full rounded border border-slate-200 bg-white px-2 text-right text-xs disabled:bg-slate-50"
                    />
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-slate-800">
                    {pricing.lineTotal == null ? (
                      <span
                        className="rounded bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-800"
                        title="No selling price yet - this line bills nothing until one is entered"
                      >
                        Unpriced
                      </span>
                    ) : (
                      money(pricing.lineTotal)
                    )}
                  </td>
                  {/* No side padding: the 32px menu button fits the 36px column, so the cell's clip never cuts it. */}
                  <td className="px-0 py-2 text-center">
                    <RowMenu
                      index={index}
                      disabled={readOnly}
                      removeReason={priceLock ? null : reason}
                      removeDisabled={priceLock || reason != null}
                      onOpen={() => onOpenLine(index)}
                      onRemove={() => onRemove(index)}
                    />
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

export default LinesGrid;
